import {ReactNode, createContext, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {captureMessage} from '@sentry/browser';
import {merge} from 'lodash';
import type {Api} from './api/api';
import {
  createCSATSurvey,
  CreateCSATSurveyRequest,
  submitCSATRating,
  SubmitCSATRatingRequest,
} from './api/csat';
import {ContactFormEndpointRequest, Profile} from '../../../generated/types';
import {
  fetchFeedback,
  FetchFeedbackRequest,
  submitFeedback,
  SubmitFeedbackRequest,
} from './api/feedback';
import {
  cancelRequestACall,
  fetchRequestACallCodes,
  fetchVerificationCodes,
  inProgressRequestACall,
  inProgressRequestACallV2,
  requestACall,
  RequestACallRequest,
} from './api/request-a-call';
import {fetchProfile, FetchProfileRequest} from './api/profile';
import {submitContactForm} from './api/email';
import {supportFetch} from '../../helpers/supportFetch';
import {fetchEligibleChannels} from './api/channels';
import {fetchTopics} from './api/topics';
import {fetchConnectPlatformTransparency} from './api/connect-platform';
import {useProfile} from '../ProfileProvider';
import {abandonChat, AbandonChatRequest} from './api/chat';

type fetchShape = typeof fetch;

export function createApi(
  fetch: fetchShape,
  {profile}: {profile?: Profile} = {},
): Api {
  const isLoggedOut = profile?.visitor.sessionType === 'anonymous';

  return {
    fetch,
    fetchEligibleChannels: (entryPoint: string, audience: string) =>
      fetchEligibleChannels(fetch, entryPoint, audience),
    fetchTopics: (entryPoint: string) => fetchTopics(fetch, entryPoint),
    fetchConnectPlatformTransparency: () =>
      fetchConnectPlatformTransparency(fetch),

    fetchProfile: (request: FetchProfileRequest) =>
      fetchProfile(
        fetch,
        request,
        !!profile?.featureFlags.irIndigoCollectFixes,
      ),

    createCSATSurvey: (request: CreateCSATSurveyRequest) =>
      createCSATSurvey(fetch, request),
    submitCSATRating: (request: SubmitCSATRatingRequest) =>
      submitCSATRating(fetch, request),

    fetchFeedback: (request: FetchFeedbackRequest) =>
      fetchFeedback(fetch, request),
    submitFeedback: (request: SubmitFeedbackRequest) =>
      submitFeedback(fetch, request),

    submitContactForm: (body: ContactFormEndpointRequest) =>
      submitContactForm(fetch, body),

    inProgressRequestACall: (entryPoint: string, audience: string) => {
      if (isLoggedOut) {
        return Promise.resolve({code: 200, data: {inProgress: false}});
      }

      return profile?.featureFlags?.racInProgressV2
        ? inProgressRequestACallV2(fetch, entryPoint)
        : inProgressRequestACall(fetch, entryPoint, audience);
    },
    inProgressRequestACallV2: (entryPoint: string) => {
      if (isLoggedOut) {
        return Promise.resolve({code: 200, data: {inProgress: false}});
      }

      return inProgressRequestACallV2(fetch, entryPoint);
    },
    requestACall: (request: RequestACallRequest) =>
      requestACall(fetch, request),
    fetchVerificationCodes: (audience: string) =>
      fetchVerificationCodes(fetch, audience),
    fetchRequestACallCodes: (entryPoint: string) => {
      if (isLoggedOut) {
        return Promise.resolve({type: 'success', pending: false});
      }

      return fetchRequestACallCodes(fetch, entryPoint);
    },
    cancelRequestACall: (entryPoint: string) =>
      cancelRequestACall(fetch, entryPoint),
    abandonChat: (request: AbandonChatRequest) => abandonChat(fetch, request),
  };
}

const ApiContext = createContext<Api>(createApi(supportFetch));

export function useApi(): Api {
  return useContext(ApiContext);
}

function authedOrAnonUrl(requiresAuth: boolean, url: string) {
  const apiMatcher = /^\/?api\/(anon\/)?(.*)$/;

  if (requiresAuth) {
    return url.replace(apiMatcher, '/api/$2');
  }

  return url.replace(apiMatcher, '/api/anon/$2');
}

export function authedOrAnonRequest(
  requiresAuth: boolean,
  input: RequestInfo | URL,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return authedOrAnonUrl(requiresAuth, input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: authedOrAnonUrl(requiresAuth, input.url),
    };
  }

  // this shouldn't ever happen, but if it ever did I'd want to know
  captureMessage(
    "ApiProvider expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

export function ApiProvider({
  children,
  fetch = supportFetch,
}: {
  children: ReactNode;
  fetch?: fetchShape;
}) {
  const {locale} = useIntl();
  const {profile} = useProfile();
  const {featureFlags} = profile;
  const {livemode} = profile;
  const {
    enableAnonSupportConversationsEndpoints: enableAnonEndpoints,
    enableSupportConversationsLivemodeHeader: enableLivemodeHeader,
    // note, this flag is surfaced from the server, returning true only if
    // ir_indigo_collect_fixes AND enable_url_revamp_active_context are true
    irIndigoCollectFixes: shouldAddAccountHeader,
  } = featureFlags;

  const requiresAuth = profile.visitor.sessionType !== 'anonymous';

  const apiFetch = useMemo(() => {
    const wrapped: fetchShape = async (input, init?) => {
      const inputWithAnonRoutes = enableAnonEndpoints
        ? authedOrAnonRequest(requiresAuth, input)
        : input;

      // stripe-livemode is used by manage
      const livemodeHeaders = enableLivemodeHeader
        ? {'stripe-livemode': livemode ? 'true' : 'false'}
        : {};

      const localeHeaders = {
        'Accept-Language': [locale, ...navigator.languages],
      };

      const headers = {
        ...livemodeHeaders,
        ...localeHeaders,
      };

      if (shouldAddAccountHeader) {
        Object.assign(headers, {'Stripe-Account': profile.merchant?.id});
      }

      const mergedInit = merge({headers}, init);

      return fetch(inputWithAnonRoutes, mergedInit);
    };

    return wrapped;
  }, [
    enableAnonEndpoints,
    enableLivemodeHeader,
    fetch,
    requiresAuth,
    livemode,
    locale,
    profile.merchant?.id,
    shouldAddAccountHeader,
  ]);

  const api = createApi(apiFetch, {profile});
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}
